import React, { useState } from "react";
import ProgressBar from "./ProgressBar";

const Info = ({ size = 18, className = "" }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="16" x2="12" y2="12" />
    <line x1="12" y1="8" x2="12.01" y2="8" />
  </svg>
);

const ProductRow = ({ product, index, isMultiple }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const isCompleted =
    product.trangThai === "Hoàn thành" ||
    product.trangThai === "Đã trả kết quả";

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  const getKetQuaText = () => {
    switch (product.trangThai) {
      case "Đã tiếp nhận":
        return "Đã tiếp nhận, đang chờ phân công xử lý hồ sơ";
      case "Chờ nộp phí":
        return product.soThongBaoNopTien
          ? `Đang chờ nộp phí cho thông báo phí số ${product.soThongBaoNopTien}`
          : "Đang chờ nộp phí cho thông báo phí.";
      case "Hoàn thành":
      case "Đã trả kết quả":
        // Chỉ hiển thị số GCN nếu có số GCN VÀ có ngày cấp GCN (khác null)
        if (product.soGCN && product.ngayCapGCN) {
          return `Hoàn thành, sản phẩm đã được cấp giấy chứng nhận hợp quy số: ${product.soGCN}`;
        }
        // Trường hợp không cấp GCN (ngayCapGCN là null) -> Báo không cấp + Ý kiến trao đổi
        const yKien = product.latestYKien?.trim();
        return `Không cấp giấy chứng nhận.${yKien ? ` ${yKien}` : ""}`;
      case "Kết thúc xử lý":
      case "Đang xử lý":
      default:
        return (
          product.latestYKien?.trim() ||
          `Hồ sơ đang ở trạng thái: ${product.trangThai}`
        );
    }
  };

  const getStatusColors = (status) => {
    switch (status) {
      case "Đã tiếp nhận":
        return {
          text: "text-blue-600",
          border: "border-blue-600",
          button: "bg-blue-600 hover:bg-blue-700",
        };
      case "Hoàn thành":
      case "Đã trả kết quả":
        return {
          text: "text-green-600",
          border: "border-green-600",
          button: "bg-green-600 hover:bg-green-700",
        };
      case "Chờ nộp phí":
        return {
          text: "text-orange-500",
          border: "border-orange-500",
          button: "bg-amber-600 hover:bg-amber-700",
        };
      case "Kết thúc xử lý":
        return {
          text: "text-red-600",
          border: "border-red-600",
          button: "bg-red-600 hover:bg-red-700",
        };
      case "Đang xử lý":
      default:
        return {
          text: "text-orange-500",
          border: "border-orange-500",
          button: "bg-orange-600 hover:bg-orange-700",
        };
    }
  };

  const getDisplayStatus = (status) => {
    const standardStatuses = [
      "Đã tiếp nhận",
      "Đang xử lý",
      "Chờ nộp phí",
      "Hoàn thành",
      "Đã trả kết quả",
      "Kết thúc xử lý",
    ];
    return standardStatuses.includes(status) ? status : "Đang xử lý";
  };

  const statusColors = getStatusColors(product.trangThai);

  const DetailItem = ({ label, value, icon }) => {
    if (!value) return null;
    return (
      <div className="flex items-start gap-3">
        <div className="text-blue-500 mt-0.5 flex-shrink-0">{icon}</div>
        <div className="flex flex-col">
          <span className="text-sm text-gray-500 mb-0.5 font-body">
            {label}
          </span>
          <span className="font-medium text-gray-900 font-body">{value}</span>
        </div>
      </div>
    );
  };

  return (
    <div className="border-b border-neutral-borderAccent last:border-b-0 hover:bg-neutral-bgDefault transition-normal">
      {/* Master View - Always Visible */}
      <div className="p-4 flex flex-col gap-4">
        {/* Top: 3-Column Layout (Left, Middle, Right) */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          {/* Left: Product Code + Deadline */}
          <div className="flex-shrink-0 md:w-1/3">
            <span className="inline-block w-auto px-3 py-0.5 text-xs font-semibold text-primary-dark bg-primary-100 rounded-full mb-1 font-body">
              {isMultiple ? `Sản phẩm ${index + 1}` : "Sản phẩm"}
            </span>
            <div className="flex flex-row items-center gap-3 md:flex-col md:items-start md:gap-1">
              <h3 className="text-base md:text-lg font-bold text-neutral-textPrimary font-heading">
                {product.kyHieu}
              </h3>
              {product.deadline && (
                <div className="flex items-center gap-1.5 text-sm text-gray-500 mt-1">
                  <svg
                    className="w-4 h-4 flex-shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <span>
                    Dự kiến trả kết quả:{" "}
                    <span className="font-medium text-gray-700">
                      {product.deadline}
                    </span>
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Middle: Progress Bar */}
          <div className="flex-1 md:mx-4">
            <ProgressBar status={product.trangThai} />
          </div>

          {/* Right: Toggle Button */}
          <button
            onClick={toggleExpand}
            className={`flex-shrink-0 flex items-center justify-center gap-2 px-4 py-2 text-sm font-semibold text-white ${statusColors.button} rounded-md transition-colors font-body`}
            aria-expanded={isExpanded}
            aria-label={isExpanded ? "Thu gọn chi tiết" : "Xem chi tiết"}
          >
            <span>{isExpanded ? "Thu gọn" : "Xem chi tiết"}</span>
            <svg
              className={`w-4 h-4 transition-transform duration-300 ${isExpanded ? "rotate-180" : "rotate-0"}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>
        </div>

        {/* Bottom: Kết quả xử lý (Full Width with matching left border) */}
        <div
          className={`w-full mt-2 pt-1 pb-2 border-l-4 ${statusColors.border} pl-4 flex items-start gap-3`}
        >
          <Info
            size={20}
            className={`${statusColors.text} mt-0.5 flex-shrink-0`}
          />
          <div
            className={`font-body text-sm md:text-base ${statusColors.text}`}
          >
            <span className="font-bold uppercase tracking-wide text-xs md:text-sm mr-2">
              Kết quả:
            </span>
            <span className="font-medium leading-relaxed">
              {getKetQuaText()}
            </span>
          </div>
        </div>
      </div>

      {/* Detail View - Collapsible with Animation */}
      <div
        className={`grid transition-all duration-300 ease-in-out ${
          isExpanded
            ? "grid-rows-[1fr] opacity-100"
            : "grid-rows-[0fr] opacity-0"
        }`}
      >
        <div className="overflow-hidden">
          <div className="px-4 pb-4">
            <div className="border-t border-gray-100 pt-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-x-6 md:gap-y-6">
                <DetailItem
                  label="Tên sản phẩm"
                  value={product.tenSanPham}
                  icon={
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14"
                      />
                    </svg>
                  }
                />

                <DetailItem
                  label="Nguồn gốc"
                  value={product.nguonGoc}
                  icon={
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  }
                />

                <DetailItem
                  label="Trạng thái hiện tại"
                  value={getDisplayStatus(product.trangThai)}
                  icon={
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  }
                />

                {product.nguoiXuLy && (
                  <DetailItem
                    label="Người xử lý"
                    value={product.nguoiXuLy}
                    icon={
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c1.306 0 2.417.835 2.83 2M9 14a3.001 3.001 0 00-2.83 2M15 11h3m-3 4h2"
                        />
                      </svg>
                    }
                  />
                )}

                {product.nguoiThamDinh && (
                  <DetailItem
                    label="Người thẩm định"
                    value={product.nguoiThamDinh}
                    icon={
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
                        />
                      </svg>
                    }
                  />
                )}
              </div>

              {isCompleted && product.soGCN && product.ngayCapGCN && (
                <div className="mt-4 pt-4 border-t-2 border-status-success/20 bg-status-success/5 rounded-md-tqc p-4">
                  <DetailItem
                    label="Số giấy chứng nhận"
                    value={product.soGCN}
                    icon={
                      <svg
                        className="w-5 h-5 text-status-success"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"
                        />
                      </svg>
                    }
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductRow;
